import { Badge, Menu, UnstyledButton } from "@mantine/core";
import { IconChevronDownOutline18 } from "nucleo-ui-outline-18";
import { createElement } from "react";
import { useTranslation } from "react-i18next";

import type { SupportedLanguage } from "@mountayaapp/locales";
import { ICON_SIZE } from "@mountayaapp/theme";

import { buildDocHref } from "../../content/rules/href";
import {
  listVisibleTabs,
  resolveTab,
  resolveTabItem,
  type Tab,
  type TabItem,
} from "../../content/rules/registry";
import { resolveDoc } from "../../content/rules/resolveDoc";
import { resolveLanguage } from "../../locale/rules/resolveLanguage";
import classes from "../docsHeader.module.css";
import { useDocsPathname } from "../providers/DocsPathnameContext";

/**
 * TabMenuProps carry the dropdown tab, the language its destinations resolve in, and the
 * item the current pathname sits under (or `null` when the reader is elsewhere).
 */
interface TabMenuProps {
  active: TabItem | null;
  isActive: boolean;
  language: SupportedLanguage;
  tab: Tab & { items: readonly TabItem[] };
}

/**
 * TabMenu is one header tab that opens a menu instead of navigating.
 *
 * The trigger is marked twice, like a link tab, but not with the same words: `data-active`
 * draws the underline, and `aria-current="true"` says it is the current item in a set.
 * Not `"page"` — the trigger is not a link to the page a reader is standing on, it is a
 * control over a set of pages, and only the item inside the dropdown is that page.
 *
 * Items are anchors, because they navigate: the docs app serves static pages with no
 * client router. A planned destination is disabled and carries a badge saying so, which is
 * what lets the roadmap be visible without offering a link to a page nobody wrote.
 */
const TabMenu = ({ active, isActive, language, tab }: TabMenuProps) => {
  const { t } = useTranslation("docs");

  return (
    // Wide enough for the longest label beside its badge. A `Menu.Item` that wraps to two
    // lines is where a bare `<Text>` would silently re-assert `md` — and `Menu` has no `size`
    // prop to correct it with — so the rows are kept to one line instead.
    <Menu position="bottom-start" shadow="md" width={280} withinPortal>
      <Menu.Target>
        <UnstyledButton
          aria-current={isActive ? "true" : undefined}
          className={classes.tab}
          data-active={isActive ? true : undefined}
        >
          {createElement(tab.icon, { size: ICON_SIZE.sm })}
          {t(tab.labelKey)}
          <IconChevronDownOutline18 size={ICON_SIZE.xs} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {tab.items.map((item) =>
          "slug" in item ? (
            <Menu.Item
              aria-current={item.id === active?.id ? "page" : undefined}
              component="a"
              href={buildDocHref(language, item.slug)}
              key={item.id}
              leftSection={createElement(item.icon, { size: ICON_SIZE.xs })}
            >
              {t(item.labelKey)}
            </Menu.Item>
          ) : (
            <Menu.Item
              disabled
              key={item.id}
              leftSection={createElement(item.icon, { size: ICON_SIZE.xs })}
              rightSection={
                <Badge color="gray" size="xs" variant="light">
                  {t("nav.sdk.planned")}
                </Badge>
              }
            >
              {t(item.labelKey)}
            </Menu.Item>
          ),
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

/**
 * DocsHeaderTabs is the header's second row and the site's top-level information
 * architecture: one entry per section, in reading order. A section with a single
 * destination is a full-reload anchor to it; a section holding several — the three API
 * references, the four SDK languages — is a dropdown, because a flat strip of every
 * destination is the list those groupings exist to avoid.
 *
 * The section that owns the current pathname is marked twice, because the two marks answer
 * different readers. `data-active` draws the underline on the header's own bottom border so
 * the strip reads as a set of tabs over the page; `aria-current` is the only one of the two a
 * screen reader hears. Without it a reader tabbing the strip meets a row of destinations with
 * nothing saying which section they are already inside; `DocsNavbar` marks its own rows the
 * same way, one level down.
 *
 * `aria-current="page"` rather than `"true"` on a link tab: the tab IS the landing page of its
 * section, and a reader on `/en/tools/` is standing on the very document the Tools tab points
 * at. A dropdown trigger is not a link at all, so it takes `"true"` and the item inside it
 * takes `"page"`.
 *
 * Only sections whose destinations resolve are listed, so a section declared in
 * the curated tree ahead of its content is simply absent until it is authored.
 *
 * The strip scrolls sideways on narrow viewports rather than folding into a menu.
 * Every section stays reachable at every width, including on the pages framed
 * without a sidebar, which carry no other navigation.
 */
export const DocsHeaderTabs = () => {
  const { t } = useTranslation("docs");
  const pathname = useDocsPathname();
  const language = resolveLanguage(pathname);
  const activeTab = resolveTab(pathname);

  // Tab ownership is a total function — an unclaimed slug falls back to the
  // Documentation tab — so the resolved tab alone would mark Documentation active
  // on a location that has no page at all. A dead URL is inside no section, so the
  // not-found view carries no active tab and the strip reads as the plain
  // information architecture it is.
  const hasPage = resolveDoc(pathname).doc !== null;
  const slug = pathname.split("/").filter(Boolean).slice(1).join("/");

  return (
    <nav aria-label={t("nav.sections")} className={classes.tabRow}>
      {listVisibleTabs(language).map((tab) => {
        const isActive = hasPage && tab.id === activeTab.id;

        if ("items" in tab) {
          return (
            <TabMenu
              active={isActive ? resolveTabItem(tab, slug) : null}
              isActive={isActive}
              key={tab.id}
              language={language}
              tab={tab}
            />
          );
        }

        return (
          <a
            aria-current={isActive ? "page" : undefined}
            className={classes.tab}
            data-active={isActive ? true : undefined}
            href={buildDocHref(language, tab.landingSlug)}
            key={tab.id}
          >
            {createElement(tab.icon, { size: ICON_SIZE.sm })}
            {t(tab.labelKey)}
          </a>
        );
      })}
    </nav>
  );
};
